import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

const resolvedDefaultSchema = z.object({
  promptTemplateId: z.string().uuid(),
  customInstructions: z.string().nullable(),
});

const scopeEnum = z.enum(['global', 'tenant', 'user']);

const defaultListItemSchema = z.object({
  id: z.string().uuid(),
  scope: scopeEnum,
  specialty: z.string().nullable(),
  language: z.string().nullable(),
  promptTemplateId: z.string().uuid(),
  promptTemplateName: z.string(),
  customInstructions: z.string().nullable(),
});

const defaultsListSchema = z.object({
  defaults: z.array(defaultListItemSchema),
});

const sourceEnum = z.enum([
  'experiment',
  'userSet',
  'adminUserSet',
  'adminTenantSet',
  'adminGlobalSet',
]);

const appointmentResolutionSchema = z.object({
  effective: z
    .object({
      promptTemplateId: z.string().uuid(),
      customInstructions: z.string().nullable(),
      source: sourceEnum,
    })
    .nullable(),

  experiment: z
    .object({
      experimentName: z.string(),
      variantName: z.string(),
      promptTemplateId: z.string().uuid().nullable(),
      outputTemplateId: z.string().uuid().nullable(),
      modelArgId: z.string().uuid().nullable(),
    })
    .nullable(),

  userSet: z
    .object({
      promptTemplateId: z.string().uuid().nullable(),
      outputTemplateId: z.string().uuid().nullable(),
      customNoteInstructions: z.string().nullable(),
    })
    .nullable(),

  adminUserSet: resolvedDefaultSchema.nullable(),
  adminTenantSet: resolvedDefaultSchema.nullable(),
  adminGlobalSet: resolvedDefaultSchema.nullable(),
});

export const promptTemplateDefaultsContract = c.router(
  {
    resolveForUser: {
      method: 'GET',
      path: '/resolve/user',
      query: z.object({
        llmOutputType: z.string(),
        language: z.string().optional(),
      }),
      responses: {
        200: resolvedDefaultSchema,
        404: z.object({ message: z.string() }),
      },
      summary:
        'Resolve prompt template default for a user (user > tenant > global)',
    },
    resolveForTenant: {
      method: 'GET',
      path: '/resolve/tenant',
      query: z.object({
        llmOutputType: z.string(),
        specialty: z.string().optional(),
        language: z.string().optional(),
      }),
      responses: {
        200: resolvedDefaultSchema,
        404: z.object({ message: z.string() }),
      },
      summary:
        'Resolve prompt template default at tenant level (tenant > global)',
    },
    resolveForAppointment: {
      method: 'GET',
      path: '/resolve/appointment/:appointmentId',
      pathParams: z.object({
        appointmentId: z.string().uuid(),
      }),
      query: z.object({
        llmOutputType: z.string().optional().default('SOAP_NOTE'),
      }),
      responses: {
        200: appointmentResolutionSchema,
        404: z.object({ message: z.string() }),
      },
      summary:
        'Resolve all prompt template layers for an appointment (experiment > user > admin-user > admin-tenant > admin-global)',
    },
    listForUser: {
      method: 'GET',
      path: '/list/user',
      query: z.object({
        llmOutputType: z.string(),
      }),
      responses: {
        200: defaultsListSchema,
      },
      summary:
        'List all applicable prompt template defaults for a user (user + tenant + global scope)',
    },
    listForTenant: {
      method: 'GET',
      path: '/list/tenant',
      query: z.object({
        llmOutputType: z.string(),
        specialty: z.string().optional(),
      }),
      responses: {
        200: defaultsListSchema,
      },
      summary:
        'List all applicable prompt template defaults at tenant level (tenant + global scope)',
    },
  },
  {
    pathPrefix: '/:tenantId/prompt-template-defaults',
  },
);
